import { useState, useEffect, useRef } from "react";
import { View, Text, XStack, YStack, Input, Spinner } from "tamagui";
import { RiBankCardLine, RiShieldCheckLine, RiCheckLine, RiInformationLine } from "react-icons/ri";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { Header, Fade, StickyBtn, detectCard, fmtCard, fmtExpiry, RADIUS } from "./Common";
import SheetWrapper from "@components/wrapper/SheetWrapper";
import { TAPPAY_SERVER_TYPE } from "@utils/tappaySdk";
import { TapPaySecurityNote } from "@components/payment/TapPayCompliance";

const TAPPAY_SCRIPT_URL = process.env.REACT_APP_TAPPAY_SCRIPT_URL || "";
const APP_ID = Number(process.env.REACT_APP_TAPPAY_APP_ID) || "";
const APP_KEY = process.env.REACT_APP_TAPPAY_APP_KEY || "";

// declare global Window.TPDirect 已集中宣告於 @utils/tappaySdk.ts，此處不再重複宣告。

export default function CardForm({ onNext, onBack }: { onNext: (info: any) => void; onBack: () => void }) {
  const [isSdkReady, setIsSdkReady] = useState(false);
  const [cardStatus, setCardStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cardName, setCardName] = useState("");
  const [saveCard, setSaveCard] = useState(true);
  const [cvvFocused, setCvvFocused] = useState(false);

  const isInitialized = useRef(false);

  useEffect(() => {
    if (isInitialized.current) return;

    const initTapPay = () => {
      if (!window.TPDirect || isInitialized.current) return;
      isInitialized.current = true;

      window.TPDirect.setupSDK(APP_ID, APP_KEY, TAPPAY_SERVER_TYPE);

      window.TPDirect.card.setup({
        fields: {
          number: {
            element: "#card-number",
            placeholder: "**** **** **** ****",
          },
          expirationDate: {
            element: "#card-expiration-date",
            placeholder: "MM / YY",
          },
          ccv: {
            element: "#card-ccv",
            placeholder: "CCV",
          },
        },
        styles: {
          input: {
            color: colors.grayDark,
            "font-size": "16px",
            "font-weight": "700",
            outline: "none",
          },
          "input.invalid": {
            color: "red",
          },
          "input.valid": {
            color: "green",
          },
        },
      });

      window.TPDirect.card.onUpdate((update: any) => {
        setCardStatus(update);
      });

      setIsSdkReady(true);
    };

    const existingScript = document.querySelector(`script[src="${TAPPAY_SCRIPT_URL}"]`);

    if (existingScript) {
      // script 已存在
      if (window.TPDirect) {
        // TPDirect 已載入，直接初始化
        initTapPay();
      } else {
        // script 存在但還在載入中
        existingScript.addEventListener("load", initTapPay, { once: true });
      }
    } else {
      // 第一次載入 script
      const script = document.createElement("script");
      script.src = TAPPAY_SCRIPT_URL;
      script.async = true;
      script.addEventListener("load", initTapPay, { once: true });
      document.body.appendChild(script);
    }
  }, []);

  const handleNext = () => {
    // 持卡人姓名改選填（TapPay getPrime 不需要，且 cardName 未送後端）
    if (!window.TPDirect || !cardStatus?.canGetPrime) return;
    
    setLoading(true);
    setError(null);

    window.TPDirect.card.getPrime((result: any) => {
      setLoading(false);
      if (result.status !== 0) {
        setError(`取得失敗: ${result.msg}`);
        return;
      }
      // 將持卡人姓名也帶過去
      onNext({ ...result, cardName });
    });
  };

  const getCardTypeInfo = (tpType: string) => {
    if (tpType === "visa") return { name: "Visa", color: "#1A1F71" };
    if (tpType === "mastercard") return { name: "Mastercard", color: "#EB001B" };
    if (tpType === "jcb") return { name: "JCB", color: "#007AC2" };
    if (tpType === "amex") return { name: "AmEx", color: "#007AC2" };
    return null;
  };

  const tpCardType = cardStatus?.cardType;
  const cardInfo = getCardTypeInfo(tpCardType);
  const cardGrad = cardInfo ? `linear-gradient(135deg, ${cardInfo.color}, ${cardInfo.color}99)` : "linear-gradient(135deg, #1E3A5F 0%, #2563EB 100%)";

  const canNext = isSdkReady && cardStatus?.canGetPrime;

  return (
    <RadiusContentWrapper padding="0" space={0}>
      <Header title="新增信用卡" sub="步驟 1 / 3 · 填寫卡片資訊" step={1} total={3} onBack={onBack} />

      <YStack p="$4" pb="$2">
        {/* Form fields */}
        <Fade i={1}>
          <YStack bg={colors.light} br={RADIUS} ov="hidden" mb="$3">
            <YStack p="$3.5" bbw={1} bbc={colors.bgGray1}>
              <XStack ai="center" jc="space-between" mb="$2">
                <XStack ai="center" gap="$2">
                  <Text fontSize={11} color={colors.gray200} fontWeight="600">
                    卡號
                  </Text>
                  {cardInfo && (
                    <Text fontSize={10} fontWeight="700" color={cardInfo.color} bg={`${cardInfo.color}18`} px="$1.5" br={6}>
                      {cardInfo.name}
                    </Text>
                  )}
                </XStack>
                {!isSdkReady && <Spinner size="small" color={colors.primary} />}
              </XStack>
              <XStack ai="center" gap="$2.5">
                <RiBankCardLine size={18} color={colors.gray200} />
                <View id="card-number" f={1} h={24} />
                {cardStatus?.status?.number === 0 && <RiCheckLine size={20} color={colors.green} />}
              </XStack>
            </YStack>

            <XStack>
              <YStack f={1} p="$3.5" brw={1} brc={colors.bgGray1}>
                <Text fontSize={11} color={colors.gray200} fontWeight="600" mb="$2">
                  到期日
                </Text>
                <View id="card-expiration-date" h={24} />
              </YStack>
              <YStack f={1} p="$3.5" bg={cvvFocused ? colors.primaryActive : "transparent"} style={{ transition: "background 0.2s" }}>
                <XStack ai="center" gap="$1.5" mb="$2">
                  <Text fontSize={11} color={colors.gray200} fontWeight="600">
                    CVV / CVC
                  </Text>
                  <RiInformationLine size={12} color={colors.gray200} />
                </XStack>
                <View id="card-ccv" h={24} />
              </YStack>
            </XStack>

            <YStack p="$3.5" btw={1} btc={colors.bgGray1}>
              <Text fontSize={11} color={colors.gray200} fontWeight="600" mb="$2">
                持卡人姓名 (英文，選填)
              </Text>
              <Input
                value={cardName}
                onChangeText={(text: string) => setCardName(text.toUpperCase())}
                placeholder="CHEN PIN YEN"
                bw={0}
                bg="transparent"
                outlineStyle="none"
                fontSize={15}
                fontWeight="700"
                color={colors.grayDark}
                ls={1}
                p={0}
                h={24}
              />
            </YStack>
          </YStack>
        </Fade>

        {error && (
          <Fade i={2}>
            <Text color="$red10" fontSize={12} mb="$3" fontWeight="bold">
              {error}
            </Text>
          </Fade>
        )}

        {/* TapPay 安全揭露（審核項 6） */}
        <Fade i={3}>
          <TapPaySecurityNote />
        </Fade>
      </YStack>

      <View p="$4" mt="auto">
        <StickyBtn
          label={loading ? "處理中..." : canNext ? "繼續 · 前往驗證" : "請填寫完整卡片資訊"}
          disabled={!canNext || loading}
          onClick={handleNext}
          icon={loading ? <Spinner color="white" /> : <RiShieldCheckLine size={18} color="#fff" />}
          shadowColor={canNext ? "#2563EB" : undefined}
        />
      </View>
    </RadiusContentWrapper>
  );
}
